import React, { useState } from 'react';
import { UserPlus, Copy, Check, RefreshCw } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

type NameOptions = {
  gender: 'male' | 'female' | 'any';
  nationality: string;
  ageRange: 'child' | 'teen' | 'adult' | 'senior';
};

export function FakeNameGenerator() {
  const [generatedName, setGeneratedName] = useState('');
  const [generatedDetails, setGeneratedDetails] = useState({
    age: '',
    birthdate: '',
    email: '',
    occupation: '',
    address: '',
  });
  const [options, setOptions] = useState<NameOptions>({
    gender: 'any',
    nationality: 'american',
    ageRange: 'adult',
  });
  const [copied, setCopied] = useState(false);

  const nationalities = [
    { value: 'american', label: 'American' },
    { value: 'british', label: 'British' },
    { value: 'french', label: 'French' },
    { value: 'german', label: 'German' },
    { value: 'spanish', label: 'Spanish' },
    { value: 'italian', label: 'Italian' },
    { value: 'chinese', label: 'Chinese' },
    { value: 'japanese', label: 'Japanese' },
    { value: 'indian', label: 'Indian' },
  ];

  const firstNames = {
    male: ['James', 'John', 'Robert', 'Michael', 'William', 'David', 'Richard', 'Joseph', 'Thomas', 'Charles'],
    female: ['Mary', 'Patricia', 'Jennifer', 'Linda', 'Elizabeth', 'Barbara', 'Susan', 'Jessica', 'Sarah', 'Karen'],
  };

  const lastNames = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez'];
  
  const occupations = ['Software Engineer', 'Teacher', 'Doctor', 'Artist', 'Writer', 'Chef', 'Architect', 'Designer', 'Lawyer', 'Accountant'];

  const generateRandomName = () => {
    const namePool = options.gender === 'any' 
      ? [...firstNames.male, ...firstNames.female]
      : firstNames[options.gender];
    
    const firstName = namePool[Math.floor(Math.random() * namePool.length)];
    const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
    const fullName = `${firstName} ${lastName}`;
    
    // Generate age based on age range
    let age;
    switch(options.ageRange) {
      case 'child':
        age = Math.floor(Math.random() * 12) + 1;
        break;
      case 'teen':
        age = Math.floor(Math.random() * 7) + 13;
        break;
      case 'adult':
        age = Math.floor(Math.random() * 40) + 20;
        break;
      case 'senior':
        age = Math.floor(Math.random() * 25) + 65;
        break;
    }

    // Generate birthdate
    const today = new Date();
    const birthYear = today.getFullYear() - age;
    const birthMonth = Math.floor(Math.random() * 12) + 1;
    const birthDay = Math.floor(Math.random() * 28) + 1;
    const birthdate = `${birthMonth}/${birthDay}/${birthYear}`;

    // Generate email
    const email = `${firstName.toLowerCase()}.${lastName.toLowerCase()}${Math.floor(Math.random() * 999)}@example.com`;

    // Generate occupation
    const occupation = occupations[Math.floor(Math.random() * occupations.length)];

    // Generate address
    const streetNumber = Math.floor(Math.random() * 9999) + 1;
    const streets = ['Main St', 'Oak Ave', 'Maple Rd', 'Cedar Ln', 'Pine Dr'];
    const cities = ['New York', 'Los Angeles', 'Chicago', 'Houston', 'Phoenix'];
    const states = ['NY', 'CA', 'IL', 'TX', 'AZ'];
    const street = streets[Math.floor(Math.random() * streets.length)];
    const city = cities[Math.floor(Math.random() * cities.length)];
    const state = states[Math.floor(Math.random() * states.length)];
    const address = `${streetNumber} ${street}, ${city}, ${state}`;

    setGeneratedName(fullName);
    setGeneratedDetails({
      age: age.toString(),
      birthdate,
      email,
      occupation,
      address,
    });
  };

  const copyToClipboard = () => {
    const textToCopy = `
Name: ${generatedName}
Age: ${generatedDetails.age}
Birthdate: ${generatedDetails.birthdate}
Email: ${generatedDetails.email}
Occupation: ${generatedDetails.occupation}
Address: ${generatedDetails.address}
    `.trim();

    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <Helmet>
        <title>Random Name Generator | DigiVault.club</title>
        <meta name="description" content="Generate random, realistic-looking names for creative writing, testing, or privacy protection. Create complete fictional identities with our free name generator." />
        <meta name="keywords" content="name generator, fake name generator, random identity generator, fictional names, character generator" />
        <link rel="canonical" href="https://digivault.club/fake-name-generator" />
      </Helmet>
      <main className="flex-grow">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <UserPlus className="w-8 h-8 text-emerald-500" />
              <h1 className="text-3xl font-bold text-white">Fake Name Generator</h1>
            </div>

            <div className="bg-gray-800 rounded-xl p-6 shadow-xl mb-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div>
                  <label className="block text-white mb-2">Gender</label>
                  <select
                    value={options.gender}
                    onChange={(e) => setOptions({ ...options, gender: e.target.value as 'male' | 'female' | 'any' })}
                    className="w-full bg-gray-700 rounded-lg px-4 py-2 text-white border-0"
                  >
                    <option value="any">Any</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
                <div>
                  <label className="block text-white mb-2">Nationality</label>
                  <select
                    value={options.nationality}
                    onChange={(e) => setOptions({ ...options, nationality: e.target.value })}
                    className="w-full bg-gray-700 rounded-lg px-4 py-2 text-white border-0"
                  >
                    {nationalities.map((nat) => (
                      <option key={nat.value} value={nat.value}>{nat.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-white mb-2">Age Range</label>
                  <select
                    value={options.ageRange}
                    onChange={(e) => setOptions({ ...options, ageRange: e.target.value as NameOptions['ageRange'] })}
                    className="w-full bg-gray-700 rounded-lg px-4 py-2 text-white border-0"
                  >
                    <option value="child">Child (1-12)</option>
                    <option value="teen">Teen (13-19)</option>
                    <option value="adult">Adult (20-59)</option>
                    <option value="senior">Senior (65+)</option>
                  </select>
                </div>
              </div>

              <button
                onClick={generateRandomName}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-4 py-3 mb-6 flex items-center justify-center gap-2 transition-colors"
              >
                <RefreshCw className="w-5 h-5" />
                Generate Random Identity
              </button>

              {generatedName && (
                <div className="bg-gray-700 rounded-lg p-6">
                  <div className="flex justify-between items-start mb-4">
                    <h2 className="text-2xl font-bold text-white">{generatedName}</h2>
                    <button
                      onClick={copyToClipboard}
                      className="bg-emerald-600 hover:bg-emerald-700 rounded-lg px-3 py-2 text-white transition-colors flex items-center gap-2"
                    >
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-gray-400">Age</p>
                      <p className="text-white">{generatedDetails.age} years old</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Birthdate</p>
                      <p className="text-white">{generatedDetails.birthdate}</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Email</p>
                      <p className="text-white">{generatedDetails.email}</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Occupation</p>
                      <p className="text-white">{generatedDetails.occupation}</p>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-gray-400">Address</p>
                      <p className="text-white">{generatedDetails.address}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-gray-800 rounded-xl p-6 shadow-xl">
              <h2 className="text-2xl font-bold mb-4 text-white">About Fake Name Generator</h2>
              <div className="space-y-4 text-gray-300">
                <p>
                  Our Fake Name Generator creates random, realistic-looking identities for various purposes:
                </p>
                <ul className="list-disc list-inside space-y-2">
                  <li>Writing and creative projects</li>
                  <li>Testing software applications</li>
                  <li>User interface demonstrations</li>
                  <li>Privacy protection in forms</li>
                  <li>Role-playing games and character development</li>
                </ul>
                <p>
                  Each generated identity includes a complete profile with name, age, birthdate, email,
                  occupation, and address. You can customize the generation process by selecting specific
                  gender, nationality, and age range preferences.
                </p>
                <div className="bg-gray-700 rounded-lg p-4 mt-4">
                  <p className="text-sm">
                    <strong>Note:</strong> All generated data is fictional and randomly created.
                    Do not use this tool for fraudulent or deceptive purposes.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}